import { useState } from 'react'
import CapstonMark from '../components/CapstonMark'
import { api } from '../lib/api'

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

// A signed-out visitor on a space URL (/discovery/<id>…), or a personal link
// that no longer works. Principals do not sign in with a magic link — the
// personal link in their onboarding email is their key — so instead of the
// login form this card offers to send that link again. The reply is the same
// neutral confirmation whether or not the address belongs to a live space, so
// the page never reveals who is a member. `notice` says why they are here when
// a dead link brought them.
export default function ResendLinkPage({ notice = null }) {
  const [email, setEmail] = useState('')
  const [fieldError, setFieldError] = useState(null)
  const [serverError, setServerError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [sentTo, setSentTo] = useState(null)

  async function handleSubmit(event) {
    event.preventDefault()
    if (submitting) return

    const trimmed = email.trim()
    let error = null
    if (!trimmed) error = 'Email is required.'
    else if (!EMAIL_RE.test(trimmed)) error = 'Enter a valid email address.'
    setFieldError(error)
    setServerError(null)
    if (error) return

    setSubmitting(true)
    try {
      await api('/api/v1/auth/resend-link', { method: 'POST', body: { email: trimmed } })
      setSentTo(trimmed)
    } catch (err) {
      // A 429 carries a message worth showing; anything else gets one honest
      // line — the endpoint itself never says whether the address is known.
      setServerError(
        err.status === 429 && err.detail
          ? err.detail
          : 'We could not send your link just now. Please try again shortly, or reply to the person who shared this space with you.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'var(--color-surface-alt)' }}
    >
      <div className="card card--panel w-full" style={{ maxWidth: '420px' }}>
        <CapstonMark size={32} />
        <p className="overline" style={{ marginTop: 'var(--space-m)' }}>
          Capston
        </p>
        <h1 className="text-h3" style={{ marginBottom: 'var(--space-m)' }}>
          {sentTo ? 'Check your email' : 'Your personal link'}
        </h1>

        {sentTo ? (
          <div className="flex flex-col" style={{ gap: 'var(--space-s)' }}>
            <p
              className="text-body-md"
              style={{ color: 'var(--color-text-secondary)' }}
              role="status"
            >
              If <strong>{sentTo}</strong> belongs to an active space, a fresh personal link is on
              its way. It opens your space directly, no sign-in needed.
            </p>
            <button type="button" className="btn btn--secondary" onClick={() => setSentTo(null)}>
              Send it again
            </button>
          </div>
        ) : (
          <>
            {notice && (
              <div
                className="alert alert--info text-body-sm"
                style={{ marginBottom: 'var(--space-s)' }}
                role="status"
              >
                {notice}
              </div>
            )}
            <p
              className="text-body-md"
              style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-s)' }}
            >
              This space opens from the personal link in your email. Lost it? Enter your address
              and we&rsquo;ll send it again.
            </p>

            {serverError && (
              <div
                className="alert alert--error text-body-sm"
                style={{ marginBottom: 'var(--space-s)' }}
                role="alert"
              >
                {serverError}
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              noValidate
              className="flex flex-col"
              style={{ gap: 'var(--space-s)' }}
            >
              <div className="field">
                <label className="field__label" htmlFor="resend-email">
                  Email
                </label>
                <input
                  id="resend-email"
                  type="email"
                  autoComplete="email"
                  className={`field__input${fieldError ? ' field__input--invalid' : ''}`}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {fieldError && <p className="field__error">{fieldError}</p>}
              </div>

              <button type="submit" className="btn btn--primary" disabled={submitting}>
                {submitting ? 'Sending…' : 'Resend my link'}
              </button>
            </form>
          </>
        )}
      </div>
    </main>
  )
}
